import { type LedgerAccount } from '../components/admin/types'

export interface DisplayAccount extends LedgerAccount {
  depth: number
  hasChildren: boolean
  isExpanded: boolean
}

/**
 * Builds a hierarchical tree structure from flat ledger accounts
 * @param accounts - Array of ledger accounts to organize into a tree
 * @param treeViewEnabled - Whether tree view is enabled
 * @param expandedPaths - Set of paths that are currently expanded
 * @returns Array of display accounts with hierarchy information
 */
export function buildTreeData (
  accounts: LedgerAccount[],
  treeViewEnabled: boolean,
  expandedPaths: Set<string>
): DisplayAccount[] {
  if (!treeViewEnabled) {
    return accounts.map(account => ({
      ...account,
      depth: 0,
      hasChildren: false,
      isExpanded: false,
    }))
  }

  // Build a map of paths to accounts
  const accountMap = new Map<string, LedgerAccount>()
  accounts.forEach(account => {
    accountMap.set(account.path, account)
  })

  // Build a map of parent paths to their children
  const childrenMap = new Map<string, string[]>()
  accounts.forEach(account => {
    const pathParts = account.path.split('.')
    if (pathParts.length > 1) {
      const parentPath = pathParts.slice(0, -1).join('.')
      if (!childrenMap.has(parentPath)) {
        childrenMap.set(parentPath, [])
      }
      childrenMap.get(parentPath)!.push(account.path)
    }
  })

  // Build the display list
  const displayAccounts: DisplayAccount[] = []

  const addAccountAndChildren = (path: string, depth: number) => {
    const account = accountMap.get(path)
    if (!account) return

    const children = childrenMap.get(path) || []
    const hasChildren = children.length > 0
    const isExpanded = expandedPaths.has(path)

    displayAccounts.push({
      ...account,
      depth,
      hasChildren,
      isExpanded,
    })

    // If expanded, add children
    if (isExpanded && hasChildren) {
      children.forEach(childPath => {
        addAccountAndChildren(childPath, depth + 1)
      })
    }
  }

  // Start with root accounts (no parent in the current set)
  accounts.forEach(account => {
    const pathParts = account.path.split('.')
    if (pathParts.length === 1) {
      // Root account
      addAccountAndChildren(account.path, 0)
    } else {
      // Check if parent exists in current set
      const parentPath = pathParts.slice(0, -1).join('.')
      if (!accountMap.has(parentPath)) {
        // Parent not in set, treat as root
        addAccountAndChildren(account.path, 0)
      }
    }
  })

  return displayAccounts
}
